// src/pages/Customer/Checkout/CheckoutPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, ChevronRight, ChevronDown, Loader2, ShoppingBag, NotebookPen, CreditCard, PackageCheck } from "lucide-react";
import { useCustomerAuth } from "../../../context/CustomerAuthContext/CustomerAuthContext";
import { useCart } from "../../../context/CartContext/CartContext";
import { addressApi, AdresDetailDto, AdresSiparisDto } from "../../../api/address";
import { orderApi } from "../../../api/order";
import { cartApi } from "../../../api/cart";
import { postRequest } from "../../../api/postRequest";

/* ================= Theme surfaces ================= */
const hexToRgb = (hex: string) => {
  const h = (hex || "#0f1115").replace("#", "");
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};
const useSurfaces = (theme?: any) => {
  const bg = theme?.backgroundColor || "#0f1115";
  const isDark = luminance(hexToRgb(bg)) < 0.5;
  return {
    bg,
    surface1: isDark ? "rgba(255,255,255,.04)" : "rgba(0,0,0,.04)",
    surface2: isDark ? "rgba(255,255,255,.07)" : "rgba(0,0,0,.06)",
    divider:  isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.12)",
    textPrimary: theme?.textPrimary ?? (isDark ? "#fff" : "#111827"),
    textSecondary: theme?.textSecondary ?? (isDark ? "rgba(255,255,255,.7)" : "#6b7280"),
    accent: theme?.accent ?? "#22c55e",
  };
};
const panel = (S: ReturnType<typeof useSurfaces>): React.CSSProperties => ({
  backgroundColor: S.surface1, borderColor: S.divider, borderWidth: 1
});
const inputBox = (S: ReturnType<typeof useSurfaces>): React.CSSProperties => ({
  backgroundColor: S.surface2, borderColor: S.divider, borderWidth: 1
});

/* ================= Utils ================= */
const fmt = (v: number, ccy = "TRY") =>
  new Intl.NumberFormat("tr-TR", { style: "currency", currency: ccy }).format(v || 0);

const CheckoutPage: React.FC = () => {
  const nav = useNavigate();
  const { customer } = useCustomerAuth();
  const { header, items, totals, refresh, clear } = useCart();

  const currency = header?.paraBirimiKodu ?? "TRY";

  // adres state
  const [list, setList] = useState<AdresSiparisDto[] | null>(null);
  const [loading, setLoading] = useState(true);

  const [selShipId, setSelShipId] = useState<number | null>(null);
  const [selBillId, setSelBillId] = useState<number | null>(null);

  const [drawerType, setDrawerType] = useState<"ship" | "bill" | null>(null);
  const [note, setNote] = useState("");

  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [odemesizAktif, setOdemesizAktif] = useState(false);

  // Guard: login zorunlu
  useEffect(() => {
    if (!customer?.id) {
      nav("/musteri/giris?return=/musteri/checkout", { replace: true });
    }
  }, [customer?.id, nav]);

  // Site ayarını çek
  useEffect(() => {
    (async () => {
      try {
        const res = await postRequest<{}, any>("/AnaEkranVerileri/listele", {});
        const raw = res?.data?.[0]?.bilgiler;
        if (res?.success && raw) {
          const parsed = JSON.parse(raw);
          setOdemesizAktif(parsed?.settings?.odemesizSiparisAktifMi === true);
        }
      } catch {}
    })();
  }, []);

  // sepet + adresleri yükle
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await refresh();
        if (customer?.id) {
          const r = await addressApi.listForOrder(customer.id);
          if (r.success && r.data) {
            setList(r.data);
            const defShip = r.data.find(x => x.varsayilanTeslimatMi) || r.data[0];
            const defBill = r.data.find(x => x.varsayilanFaturaMi) || r.data[0];
            setSelShipId(defShip ? defShip.adresId : null);
            setSelBillId(defBill ? defBill.adresId : null);
          } else {
            setList([]);
          }
        }
      } catch (e: any) {
        setError(e?.message || "Yüklenemedi.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id]);

  const subtotal = totals?.toplamNet ?? 0;
  const tax = totals?.toplamVergi ?? 0;
  const grand = totals?.toplamBrut ?? 0;

  const isCartEmpty = (items?.length || 0) === 0;

//   const onPlaceOrder = async () => {
//     if (!customer?.id) return;
//     if (isCartEmpty) { setError("Sepet boş."); return; }
//     if (!selShipId || !selBillId) { setError("Lütfen teslimat ve fatura adresi seçin."); return; }

//     setPlacing(true);
//     setError(null);
//     try {
//       // seçilen adreslerin detayını çekip JSON string hazırla
//       const [shipRes, billRes] = await Promise.all([
//         addressApi.detail(customer.id, selShipId),
//         addressApi.detail(customer.id, selBillId),
//       ]);

//       const ship: AdresDetailDto | undefined = shipRes.success ? shipRes.data! : undefined;
//       const bill: AdresDetailDto | undefined = billRes.success ? billRes.data! : undefined;

//       const payload = {
//         musteriId: customer.id,
//         paraBirimiKodu: currency,
//         faturaAdresiJson: bill ? JSON.stringify(bill) : null,
//         teslimatAdresiJson: ship ? JSON.stringify(ship) : null,
//         notlar: note || null,
//         kargoUcretiNet: 0,
//         items: items.map(it => ({
//           urunId: it.urunId,
//           varyantId: it.varyantId,
//           urunAdi: it.urunAdi ?? `Ürün #${it.urunId}`,
//           secenekOzeti: it.secenekOzeti ?? null,
//           adet: it.adet,
//           birimFiyatNet: it.birimFiyatNet,
//           vergiOraniYuzde: it.vergiOraniYuzde ?? 0,
//           satirIndirimNet: it.satirIndirimNet ?? 0,
//         })),
//       } satisfies Parameters<typeof orderApi.create>[0];

//       const res = await orderApi.create(payload);
//       if (!res.success || !res.data) throw new Error(res.errorDetail || "Sipariş oluşturulamadı.");

//       // sepeti kapat/temizle
// // onPlaceOrder içinde, başarılı sipariş sonrası:
// if (header?.sepetId) {
//   try { await cartApi.checkout({ sepetId: header.sepetId }); } catch {}
// }
// await clear();

// // ⬇️ React Router nav yerine tam sayfa yenile
// const successUrl =
//   `/musteri/checkout/basarili?no=${encodeURIComponent(res.data.siparisNo)}&id=${res.data.siparisId}`;
// window.location.assign(successUrl);


//     } catch (e: any) {
//       setError(e?.message || "Sipariş verilemedi.");
//     } finally {
//       setPlacing(false);
//     }
//   };
const onPlaceOrder = async () => {
  setError(null);

  if (!customer?.id) {
    nav("/musteri/giris?return=/musteri/checkout", { replace: true });
    return;
  }
  if ((items?.length || 0) === 0) {
    setError("Sepet boş.");
    return;
  }
  if (!selShipId || !selBillId) {
    setError("Lütfen teslimat ve fatura adresi seçin.");
    return;
  }

  setPlacing(true);
  try {
    const [shipRes, billRes] = await Promise.all([
      addressApi.detail(customer.id, selShipId),
      addressApi.detail(customer.id, selBillId),
    ]);

    const ship = shipRes.success ? shipRes.data : null;
    const bill = billRes.success ? billRes.data : null;

    const stash = {
      shipAddressId: selShipId,
      billAddressId: selBillId,
      note: note || null,
      currency,
    
      // ✅ doğru eşleşme
      faturaAdresiJson: billRes.success && billRes.data ? JSON.stringify(billRes.data) : null,
      teslimatAdresiJson: shipRes.success && shipRes.data ? JSON.stringify(shipRes.data) : null,
    
      items: items.map(it => ({
        urunId: it.urunId,
        varyantId: it.varyantId,
        urunAdi: it.urunAdi ?? `Ürün #${it.urunId}`,
        adet: it.adet,
        birimFiyatNet: it.birimFiyatNet,
        vergiOraniYuzde: it.vergiOraniYuzde ?? 0,
        satirIndirimNet: it.satirIndirimNet ?? 0,
      })),
    };

    try {
      localStorage.setItem("checkout_context", JSON.stringify(stash));
    } catch {}

    const ok = `${window.location.origin}/musteri/odeme/basarili`;
    const fail = `${window.location.origin}/musteri/odeme/hata`;

    nav(`/musteri/odeme/paytr?ok=${encodeURIComponent(ok)}&fail=${encodeURIComponent(fail)}`);
  } catch (e: any) {
    setError(e?.message || "Ödeme başlatılamadı.");
  } finally {
    setPlacing(false);
  }
};

  // Ödemesiz doğrudan sipariş
  const onPlaceOrderDirect = async () => {
    setError(null);
    if (!customer?.id) { nav("/musteri/giris?return=/musteri/checkout", { replace: true }); return; }
    if ((items?.length || 0) === 0) { setError("Sepet boş."); return; }
    if (!selShipId || !selBillId) { setError("Lütfen teslimat ve fatura adresi seçin."); return; }

    setPlacing(true);
    try {
      const [shipRes, billRes] = await Promise.all([
        addressApi.detail(customer.id, selShipId),
        addressApi.detail(customer.id, selBillId),
      ]);

      const payload = {
        musteriId: customer.id,
        paraBirimiKodu: currency,
        faturaAdresiJson: billRes.success && billRes.data ? JSON.stringify(billRes.data) : null,
        teslimatAdresiJson: shipRes.success && shipRes.data ? JSON.stringify(shipRes.data) : null,
        notlar: note || null,
        kargoUcretiNet: 0,
        items: items.map(it => ({
          urunId: it.urunId,
          varyantId: it.varyantId,
          urunAdi: it.urunAdi ?? `Ürün #${it.urunId}`,
          secenekOzeti: it.secenekOzeti ?? null,
          adet: it.adet,
          birimFiyatNet: it.birimFiyatNet,
          vergiOraniYuzde: it.vergiOraniYuzde ?? 0,
          satirIndirimNet: it.satirIndirimNet ?? 0,
        })),
      };

      const res = await orderApi.create(payload);
      if (!res.success || !res.data) throw new Error(res.errorDetail || "Sipariş oluşturulamadı.");

      if (header?.sepetId) {
        try { await cartApi.checkout({ sepetId: header.sepetId }); } catch {}
      }
      await clear();

      window.location.assign(
        `/musteri/checkout/basarili?no=${encodeURIComponent(res.data.siparisNo)}&id=${res.data.siparisId}`
      );
    } catch (e: any) {
      setError(e?.message || "Sipariş verilemedi.");
    } finally {
      setPlacing(false);
    }
  };

  const AddressCard: React.FC<{
    title: string;
    selected?: AdresSiparisDto | null;
    onPick: () => void;
  }> = ({ title, selected, onPick }) => (
    <div className="rounded-xl p-3 md:p-4 border" >
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-2">
          <MapPin size={18} />
          <div className="font-medium" >{title}</div>
        </div>
        <button
          onClick={onPick}
          className="text-sm inline-flex items-center gap-1 px-2 h-9 rounded-md border"
          
        >
          Değiştir <ChevronRight size={14} />
        </button>
      </div>

      {selected ? (
        <div className="text-sm">
          <div className="font-medium" >
            {selected.adSoyad || "—"}
          </div>
          <div>{selected.adresText || "—"}</div>
          <div>{[selected.ilce, selected.sehir].filter(Boolean).join(" / ")}</div>
          <div>{selected.telefon || ""}</div>
        </div>
      ) : (
        <div className="text-sm">Adres seçilmemiş.</div>
      )}
    </div>
  );

  const selectedShip = useMemo(
    () => list?.find(a => a.adresId === selShipId) || null,
    [list, selShipId]
  );
  const selectedBill = useMemo(
    () => list?.find(a => a.adresId === selBillId) || null,
    [list, selBillId]
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 md:py-10" >
      <h1 className="text-xl md:text-2xl font-normal  mb-4" >
        Ödeme
      </h1>

      {!!error && (
        <div
          className="mb-4 px-3 py-2 rounded-md border text-sm"
          style={{ backgroundColor: "rgba(239,68,68,.10)", borderColor: "rgba(239,68,68,.35)", color: "#b91c1c" }}
        >
          {error}
        </div>
      )}

      {/* Loading / Empty */}
      {loading ? (
        <div className="rounded-xl p-8 text-center border" >
          <Loader2 className="animate-spin inline-block mr-2" /> <span>Yükleniyor…</span>
        </div>
      ) : isCartEmpty ? (
        <div className="rounded-xl p-8 text-center border" >
          <span>Sepetiniz boş. </span>
          <button className="underline"  onClick={() => nav("/products")}>Ürünlere dön</button>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-6">
          {/* Sol: Adresler + Not */}
          <section className="col-span-12 lg:col-span-7 space-y-4">
            <AddressCard
              title="Teslimat Adresi"
              selected={selectedShip}
              onPick={() => setDrawerType("ship")}
            />
            <AddressCard
              title="Fatura Adresi"
              selected={selectedBill}
              onPick={() => setDrawerType("bill")}
            />

            {/* Not */}
            <div className="rounded-xl p-3 md:p-4 border" >
              <div className="inline-flex items-center gap-2 mb-2">
                <NotebookPen size={18} />
                <div className="font-medium" >Sipariş Notu (opsiyonel)</div>
              </div>
              <textarea
                className="w-full rounded-md p-2 text-sm border"
                rows={3}
                placeholder="Kargo için ek not bırakabilirsiniz…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
          </section>

          {/* Sağ: Özet */}
          <aside className="col-span-12 lg:col-span-5">
            <div className="rounded-2xl p-4 md:p-5 space-y-4 border" >
              <div className="flex items-center gap-2 font-semibold" >
                <ShoppingBag size={18} /> Sipariş Özeti
              </div>

              {/* Items küçük liste */}
              <div className="max-h-56 overflow-auto" style={{ borderTop: `1px solid `, borderBottom: `1px solid ` }}>
                {items.map((it) => (
                  <div key={it.sepetKalemId} className="py-3 text-sm" style={{ borderBottom: `1px dashed ` }}>
                    <div className="flex justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate" >
                          {it.urunAdi ?? `Ürün #${it.urunId}`}
                        </div>
                        {!!it.secenekOzeti && (
                          <div className="truncate">{it.secenekOzeti}</div>
                        )}
                      </div>
                      <div className="text-right">
                        <div>{it.adet} adet</div>
                        <div className="font-medium" >{fmt(it.birimFiyatNet, currency)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="pt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Ara Toplam</span>
                  <span >{fmt(subtotal, currency)}</span>
                </div>
                {/* <div className="flex justify-between">
                  <span>KDV</span>
                  <span >{fmt(tax, currency)}</span>
                </div> */}
                <div className="flex justify-between text-base font-semibold">
                  <span >Genel Toplam</span>
                  <span >{fmt(grand, currency)}</span>
                </div>
              </div>

              {odemesizAktif ? (
                <div className="flex flex-col gap-2">
                  <button
                    className="w-full h-11 rounded-md bg-orange-500 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                    onClick={onPlaceOrder}
                    disabled={placing || !selShipId || !selBillId}
                  >
                    {placing ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
                    Ödeme Yap
                  </button>
                  <button
                    className="w-full h-11 rounded-md border-2 border-orange-500 text-orange-600 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-orange-50"
                    onClick={onPlaceOrderDirect}
                    disabled={placing || !selShipId || !selBillId}
                  >
                    {placing ? <Loader2 className="animate-spin" size={16} /> : <PackageCheck size={16} />}
                    Ödemesiz Sipariş Ver
                  </button>
                </div>
              ) : (
                <button
                  className="w-full h-11 rounded-md bg-orange-500 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  onClick={onPlaceOrder}
                  disabled={placing || !selShipId || !selBillId}
                >
                  {placing ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />}
                  Ödeme Yap
                </button>
              )}
            </div>
          </aside>
        </div>
      )}

      {/* Drawer: adres seçimi */}
      <AnimatePresence>
        {drawerType && (
          <>
            <motion.div
              className="fixed inset-0 z-40"
              style={{  }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDrawerType(null)}
            />
            <motion.div
              className="fixed bottom-0 left-0 right-0 z-50 p-4 rounded-t-2xl"
              initial={{ y: 40 }}
              animate={{ y: 0 }}
              exit={{ y: 40 }}
              transition={{ type: "tween", duration: 0.2 }}
            >
              <div className="mx-auto max-w-2xl">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-semibold" >
                    {drawerType === "ship" ? "Teslimat Adresi Seç" : "Fatura Adresi Seç"}
                  </div>
                  <ChevronDown className="opacity-60" />
                </div>

                {(!list || list.length === 0) && (
                  <div className="text-sm rounded-lg p-3 border" >
                    <span>Kayıtlı adres yok. </span>
                    <button
                      className="underline"
                      
                      onClick={() => {
                        setDrawerType(null);
                        nav("/musteri/panel/adresler");
                      }}
                    >
                      Adres ekle
                    </button>
                  </div>
                )}

                <div className="space-y-2 max-h-80 overflow-auto">
                  {list?.map((a) => {
                    const checked = (drawerType === "ship" ? selShipId : selBillId) === a.adresId;
                    return (
                      <label key={a.adresId} className="block rounded-lg p-3 border cursor-pointer" >
                        <div className="flex items-start gap-3">
                          <input
                            type="radio"
                            className="mt-1"
                            checked={checked}
                            onChange={() => {
                              if (drawerType === "ship") setSelShipId(a.adresId);
                              else setSelBillId(a.adresId);
                            }}
                          />
                          <div className="text-sm">
                            <div className="font-medium" >
                              {a.baslik || "Adres"}{" "}
                              {a.varsayilanTeslimatMi && <span className="ml-1 text-xs" style={{ color: "#059669" }}>(Vars. Teslimat)</span>}
                              {a.varsayilanFaturaMi && <span className="ml-1 text-xs" style={{ color: "#2563eb" }}>(Vars. Fatura)</span>}
                            </div>
                            <div>{a.adSoyad}</div>
                            <div>{a.adresText}</div>
                            <div>{[a.ilce, a.sehir].filter(Boolean).join(" / ")}</div>
                            <div>{a.telefon}</div>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>

                <div className="mt-3 flex gap-2">
                  <button
                    className="flex-1 h-10 rounded-md border"
                    
                    onClick={() => setDrawerType(null)}
                  >
                    Kapat
                  </button>
                  <button
                    className="flex-1 h-10 rounded-md text-white"
                    onClick={() => setDrawerType(null)}
                  >
                    Seçimi Kaydet
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CheckoutPage;
